"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { ArrowRight } from "lucide-react";

/**
 * Travel Connected — homepage FAQ preview (2-column accordion) per the
 * mockups. Server/static. Neutral, supportable answers.
 */
export default function FaqPreview() {
  const { t } = useTranslations();

  const faqs = [
    { q: t("pc.faq.q1", "What is an eSIM?"), a: t("pc.faq.a1", "An eSIM is a digital SIM you activate by scanning a QR code — no physical card, ideal for travel.") },
    { q: t("pc.faq.q2", "Will my phone work?"), a: t("pc.faq.a2", "Most phones released after 2018 support eSIM. Use our compatibility checker to confirm yours in seconds.") },
    { q: t("pc.faq.q3", "How do I activate my eSIM?"), a: t("pc.faq.a3", "Open the email we send, scan the QR code in your phone settings, and turn on data at your destination.") },
    { q: t("pc.faq.q4", "Can I use hotspot/tethering?"), a: t("pc.faq.a4", "Hotspot is supported on most plans, so you can share your connection with other devices.") },
    { q: t("pc.faq.q5", "Can I keep my number?"), a: t("pc.faq.a5", "Yes. The eSIM is data-only and runs alongside your home SIM, so you keep your number for calls and messages.") },
    { q: t("pc.faq.q6", "What if I have an issue?"), a: t("pc.faq.a6", "Our support team is available 24/7, and every order is covered by a money-back guarantee.") },
  ];

  return (
    <section className="container mx-auto px-4 py-12">
      <Reveal as="div" className="flex items-end justify-between gap-4 mb-6" y={16}>
        <h2 className="text-2xl sm:text-3xl text-foreground">{t("pc.faq.title", "Frequently asked questions")}</h2>
        <Link href="/contact-us/" className="inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline shrink-0 group/help">
          {t("pc.faq.more", "Visit help center")} <ArrowRight className="w-4 h-4 transition-transform group-hover/help:translate-x-0.5" />
        </Link>
      </Reveal>

      <Stagger as="div" className="grid md:grid-cols-2 gap-3" staggerChildren={0.05} amount={0.15}>
        {faqs.map(({ q, a }) => (
          <StaggerItem as="div" key={q} y={12}>
            <details className="group pc-card p-4 transition-colors hover:border-primary/40">
              <summary className="flex items-center justify-between cursor-pointer font-semibold text-foreground list-none">
                {q}<span className="text-primary group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-2 text-sm text-muted-foreground">{a}</p>
            </details>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}
